/**
 * Care Team Export modal — IronLimb Session D.
 *
 * Opens from the Residual-Limb Log card on Profile. Lets the user pick a
 * date range (start + end + quick-preset chips), pulls the relevant data
 * from the backend + profile + local sources, generates a PDF client-side,
 * and triggers a download.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Download, X } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { PROSTHESIS_VARIANTS } from '../data/prosthesisVariants';
import { todayLocalISO } from '../data/residualLimbLog';
import CareTeamPDF from './CareTeamPDF';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isoMinusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const CareTeamExportModal = ({ open, onOpenChange }) => {
  const { profile } = useProfile();
  const [start, setStart] = useState(isoMinusDays(30));
  const [end, setEnd] = useState(todayLocalISO());
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStart(isoMinusDays(30));
      setEnd(todayLocalISO());
      setError('');
    }
  }, [open]);

  const setPreset = (n) => {
    if (n === 'all') {
      setStart('2024-01-01');
    } else {
      setStart(isoMinusDays(n));
    }
    setEnd(todayLocalISO());
  };

  const inRange = (dateStr) => dateStr >= start && dateStr <= end;

  const generate = async () => {
    if (start > end) {
      setError('Start date must be on or before end date.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      // 1. Residual-limb logs in range
      let residualLogs = [];
      try {
        const r = await axios.get(`${API}/residual-limb-logs`);
        residualLogs = (r.data?.logs || []).filter((l) => inRange(l.date));
      } catch { /* tolerate no data */ }

      // 2. Pain check-ins from profile (local source). Profile keeps
      //    `painHistory: [{date, level, ...}]` per the existing pain-check
      //    pattern. If shape differs we pass an empty array.
      let painLogs = [];
      if (Array.isArray(profile.painHistory)) {
        painLogs = profile.painHistory.filter((p) => p && p.date && inRange(p.date));
      } else if (Array.isArray(profile.painCheckIns)) {
        painLogs = profile.painCheckIns.filter((p) => p && p.date && inRange(p.date));
      }

      // 3. Activity counts. Pulled from profile.workouts + training/endurance sessions
      //    via local sources for now.
      const strengthCount = Array.isArray(profile.workouts)
        ? profile.workouts.filter((w) => w.date && inRange(w.date)).length
        : 0;
      const enduranceCount = Array.isArray(profile.recoverySessions)
        ? profile.recoverySessions.filter((s) => s.date && inRange(s.date)).length
        : 0;
      const combativesCount = Array.isArray(profile.trainingSessions)
        ? profile.trainingSessions.filter((s) => s.date && inRange(s.date)).length
        : 0;

      const variantLabels = (profile.prosthesisVariants || []).map((id) =>
        PROSTHESIS_VARIANTS.find((v) => v.id === id)?.label || id
      );

      const blob = await pdf(
        <CareTeamPDF
          profile={profile}
          variantLabels={variantLabels}
          submodes={profile.adaptiveSubmodes || []}
          startDate={start}
          endDate={end}
          residualLogs={residualLogs}
          painLogs={painLogs}
          activityCounts={{ strength: strengthCount, endurance: enduranceCount, combatives: combativesCount }}
          generatedAt={new Date().toLocaleString()}
        />
      ).toBlob();

      const filename = `enferno-care-summary-${start}-to-${end}.pdf`;
      saveAs(blob, filename);
      onOpenChange(false);
    } catch (e) {
      setError('PDF generation failed. Try a shorter date range.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="care-team-export-modal">
        <DialogHeader>
          <DialogTitle>Care team summary</DialogTitle>
          <DialogDescription>
            Pick a date range. We'll generate a printable PDF you can share with your PT, prosthetist, or clinician. All data stays on your device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Date range</div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                max={end}
                data-testid="care-team-start-date"
                className="flex-1 bg-enf-surface-mid border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary"
              />
              <span className="text-enf-text-muted text-xs">to</span>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                min={start}
                max={todayLocalISO()}
                data-testid="care-team-end-date"
                className="flex-1 bg-enf-surface-mid border border-enf-surface-border rounded-md p-2 text-sm text-enf-text-primary"
              />
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">Quick presets</div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: 'Last 7', n: 7 },
                { label: 'Last 30', n: 30 },
                { label: 'Last 90', n: 90 },
                { label: 'All time', n: 'all' },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setPreset(p.n)}
                  data-testid={`care-team-preset-${p.label.toLowerCase().replace(' ', '-')}`}
                  className="px-3 py-1.5 rounded-full text-xs border bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-adaptive-accent/40 hover:text-adaptive-accent transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="text-xs text-red-400">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-md text-sm border border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary"
            >
              <X className="w-4 h-4 inline mr-1" /> Cancel
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              data-testid="care-team-generate-button"
              className="px-4 py-2 rounded-md text-sm font-semibold bg-adaptive-accent hover:bg-adaptive-accent-hover text-enf-text-primary disabled:opacity-50"
            >
              <Download className="w-4 h-4 inline mr-1" /> {generating ? 'Generating…' : 'Generate PDF'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CareTeamExportModal;
